// ts-rest contract for the EMPLOYEE side of PA Dealing (SEP-264): "my
// requests", the questionnaire, the security search, and the per-user
// notification inbox.
//
// Every read is scoped to the caller. There is no employee id on any request
// here, and there can never be one: the server takes the owner from the
// session (`paOwnerScopeOf` in `apps/server/src/modules/pa-dealing/pa-owner-scope.ts`).
//
// Two routes are raw Nest and not ts-rest, because ts-rest is JSON-only:
//   POST /api/pa-dealing/requests/:id/documents          multipart upload
//   GET  /api/pa-dealing/requests/:id/documents/:documentId/content   bytes

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
    myPaQuestionnaireQuerySchema,
    myPaQuestionnaireResponseSchema,
    myPaTradeRequestSchema,
    myPaTradeRequestsResponseSchema,
    paCommentBodySchema,
    paCommentSchema,
    paSecuritySearchResponseSchema,
    paTradeFieldsBodySchema,
    respondToPaInformationRequestBodySchema,
    submitPaTradeRequestResponseSchema,
    updatePaTradeRequestBodySchema,
    userNotificationsResponseSchema,
    withdrawPaTradeRequestBodySchema,
} from '../schemas/pa-dealing';
import { errorBody, errorBodyWithCode } from '../schemas/common';

const c = initContract();

const idParams = z.object({ id: z.string() });

export const paDealingContract = c.router(
    {
        listMyRequests: {
            method: 'GET',
            path: '/pa-dealing/requests',
            responses: { 200: myPaTradeRequestsResponseSchema },
            summary: 'The PA trade requests of the caller, newest first',
        },
        getMyRequest: {
            method: 'GET',
            path: '/pa-dealing/requests/:id',
            pathParams: idParams,
            responses: { 200: myPaTradeRequestSchema, 404: errorBody },
            summary: 'One PA trade request of the caller',
        },
        createRequest: {
            method: 'POST',
            path: '/pa-dealing/requests',
            body: paTradeFieldsBodySchema,
            responses: { 201: myPaTradeRequestSchema, 400: errorBodyWithCode },
            summary: 'Start a draft PA trade request',
        },
        updateRequest: {
            method: 'PATCH',
            path: '/pa-dealing/requests/:id',
            pathParams: idParams,
            body: updatePaTradeRequestBodySchema,
            responses: {
                200: myPaTradeRequestSchema,
                400: errorBodyWithCode,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Edit a draft PA trade request',
        },
        submitRequest: {
            method: 'POST',
            path: '/pa-dealing/requests/:id/submit',
            pathParams: idParams,
            body: z.object({}).optional(),
            responses: {
                200: submitPaTradeRequestResponseSchema,
                400: errorBodyWithCode,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Submit a draft: the rules engine runs and the request lands in its state',
        },
        withdrawRequest: {
            method: 'POST',
            path: '/pa-dealing/requests/:id/withdraw',
            pathParams: idParams,
            body: withdrawPaTradeRequestBodySchema,
            responses: { 200: myPaTradeRequestSchema, 404: errorBody, 409: errorBodyWithCode },
            summary: 'Withdraw a draft, pending or information-requested request',
        },
        respondToInformationRequest: {
            method: 'POST',
            path: '/pa-dealing/requests/:id/respond',
            pathParams: idParams,
            body: respondToPaInformationRequestBodySchema,
            responses: { 200: myPaTradeRequestSchema, 404: errorBody, 409: errorBodyWithCode },
            summary: 'Answer an information request; the request returns to Compliance',
        },
        addComment: {
            method: 'POST',
            path: '/pa-dealing/requests/:id/comments',
            pathParams: idParams,
            body: paCommentBodySchema,
            responses: { 201: paCommentSchema, 404: errorBody, 409: errorBodyWithCode },
            summary: 'Comment on one of my requests (always employee_visible)',
        },
        questionnaire: {
            method: 'GET',
            path: '/pa-dealing/questionnaire',
            query: myPaQuestionnaireQuerySchema,
            responses: { 200: myPaQuestionnaireResponseSchema },
            summary: 'The questions the caller answers for a side, plus the house-list hint',
        },
        searchSecurities: {
            method: 'GET',
            path: '/pa-dealing/securities',
            query: z.object({ q: z.string().min(1) }),
            responses: { 200: paSecuritySearchResponseSchema },
            summary: 'Search a security by ISIN, SEDOL, ticker or name',
        },
        listMyNotifications: {
            method: 'GET',
            path: '/notifications/me',
            query: z.object({ unreadOnly: z.enum(['true', 'false']).optional() }).optional(),
            responses: { 200: userNotificationsResponseSchema },
            summary: 'The in-app notifications of the caller, newest first',
        },
        markNotificationRead: {
            method: 'POST',
            path: '/notifications/me/:id/read',
            pathParams: idParams,
            body: z.object({}).optional(),
            responses: { 200: z.object({ ok: z.literal(true) }), 404: errorBody },
            summary: 'Mark one notification of the caller as read',
        },
        markAllNotificationsRead: {
            method: 'POST',
            path: '/notifications/me/read-all',
            body: z.object({}).optional(),
            responses: { 200: z.object({ ok: z.literal(true), marked: z.number().int() }) },
            summary: 'Mark every notification of the caller as read',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
